/**
 * lib/geo/flagMeta.ts
 * ─────────────────────────────────────────────────────────────────────────────
 * Country and region flag emoji metadata for RHEA Experiences.
 *
 * Exports:
 *   getRegionFlag(regionKey)  → flag emoji string | ""
 *   getCountryFlag(country)   → flag emoji string | ""
 *   getCityFlag(iata)         → flag emoji string | ""
 *
 * Used by:
 *   routes/experiences.tsx — teaser cards, journey hero, city chapter meta
 *
 * Note: Benelux spans two countries (NL + BE) — returns combined "🇳🇱🇧🇪".
 */

// ─── Region → flag(s) ─────────────────────────────────────────────────────────
// Keys match canonical ROUTE_VARIANTS keys from route_registry.

const REGION_FLAG_MAP: Readonly<Record<string, string>> = {
  "japon":        "🇯🇵",
  "japan":        "🇯🇵",
  "portugal":     "🇵🇹",
  "andalucia":    "🇪🇸",
  "andalucía":    "🇪🇸",
  "italia":       "🇮🇹",
  "italy":        "🇮🇹",
  "espana norte": "🇪🇸",
  "españa norte": "🇪🇸",
  "benelux":      "🇳🇱🇧🇪",
};

// ─── Country display name → flag ──────────────────────────────────────────────
// Covers all countries present in EXPERIENCES_CITIES + common user-typed names.

const COUNTRY_FLAG_MAP: Readonly<Record<string, string>> = {
  // Japan
  "japón":        "🇯🇵",
  "japon":        "🇯🇵",
  "japan":        "🇯🇵",
  // Portugal
  "portugal":     "🇵🇹",
  // Spain
  "españa":       "🇪🇸",
  "espana":       "🇪🇸",
  "spain":        "🇪🇸",
  // Italy
  "italia":       "🇮🇹",
  "italy":        "🇮🇹",
  // France
  "francia":      "🇫🇷",
  "france":       "🇫🇷",
  // Mexico
  "méxico":       "🇲🇽",
  "mexico":       "🇲🇽",
  // Colombia
  "colombia":     "🇨🇴",
  // Netherlands / Belgium (standalone)
  "países bajos": "🇳🇱",
  "holanda":      "🇳🇱",
  "bélgica":      "🇧🇪",
  "belgica":      "🇧🇪",
};

// ─── IATA → flag ──────────────────────────────────────────────────────────────

const CITY_FLAG_MAP: Readonly<Record<string, string>> = {
  // Japan
  TYO: "🇯🇵",
  KYO: "🇯🇵",
  OSA: "🇯🇵",
  HKN: "🇯🇵",
  KNZ: "🇯🇵",
  // Spain
  BCN: "🇪🇸",
  MAD: "🇪🇸",
  SEV: "🇪🇸",
  GRA: "🇪🇸",
  COR: "🇪🇸",
  TOL: "🇪🇸",
  AGP: "🇪🇸",
  CDZ: "🇪🇸",
  // Portugal
  LIS: "🇵🇹",
  OPO: "🇵🇹",
  CBM: "🇵🇹",
  FAO: "🇵🇹",
  DOV: "🇵🇹",
  // Mexico
  MEX: "🇲🇽",
  // France
  CDG: "🇫🇷",
  // Netherlands
  AMS: "🇳🇱",
  // Belgium
  BGE: "🇧🇪",
  // Italy
  ROM: "🇮🇹",
  FLR: "🇮🇹",
  VCE: "🇮🇹",
  NAP: "🇮🇹",
  MXP: "🇮🇹",
};

// ─── Public API ───────────────────────────────────────────────────────────────

/**
 * Return the flag emoji for a canonical region key.
 * Keys are matched case-insensitively.
 * Returns "" when the region is not recognized.
 *
 * @example
 *   getRegionFlag("japon")       // "🇯🇵"
 *   getRegionFlag("benelux")     // "🇳🇱🇧🇪"
 *   getRegionFlag("italia")      // "🇮🇹"
 */
export function getRegionFlag(regionKey: string): string {
  return REGION_FLAG_MAP[regionKey.toLowerCase().trim()] ?? "";
}

/**
 * Return the flag emoji for a country display name.
 * Keys are matched case-insensitively.
 * Returns "" when the country is not in the map.
 *
 * @example
 *   getCountryFlag("Japón")      // "🇯🇵"
 *   getCountryFlag("Francia")    // "🇫🇷"
 *   getCountryFlag("Colombia")   // "🇨🇴"
 */
export function getCountryFlag(country: string): string {
  return COUNTRY_FLAG_MAP[country.toLowerCase().trim()] ?? "";
}

/**
 * Return the flag emoji for a city IATA code.
 * Returns "" when the IATA is not recognized.
 *
 * @example
 *   getCityFlag("TYO")    // "🇯🇵"
 *   getCityFlag("LIS")    // "🇵🇹"
 *   getCityFlag("MXP")    // "🇮🇹"
 */
export function getCityFlag(iata: string): string {
  return CITY_FLAG_MAP[iata.toUpperCase().trim()] ?? "";
}
